import { useState, useEffect, useCallback, useMemo } from 'react'
import { getUsuarios, cambiarEstadoUsuario, actualizarUsuario, resetearClaveUsuario, getEquipos, getTiposUsuario } from '../api/apiConfig'
import { chileLocalDigits, chileMobileCompleto, chileMobileDigits, formatChileMobile } from '../utils/phoneChile'
import { MdEdit, MdLock, MdPersonAdd, MdSearch } from 'react-icons/md'
import { filterData } from '../utils/tableSortSearch'
import { useUser } from '../context/UserContext'
import NuevoUsuario from './NuevoUsuario'

const PAGE_SIZES = [10, 20, 50]
const MIN_LEN_CLAVE = 8
const EMPTY_PASSWORD_FORM = { nuevo: '', confirmar: '', operador: '' }

const SUCURSALES = [
  'Antofagasta', 'Calama', 'Concepción', 'Copiapó', 'Digital', 'Iquique',
  'La Serena', 'Normalización', 'Puerto Montt', 'Punta Arenas', 'Rancagua',
  'Santiago', 'Talca', 'Temuco', 'Viña del Mar',
]

const TIPO_EJECUTIVO = ['Agente-Empresa', 'Digital', 'Pyme', 'Otros']

const INPUT_CLASS = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm'

function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k]
    if (v != null && String(v).trim() !== '') return v
  }
  return ''
}

function fechaInput(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  return ''
}

function telefonoTabla(value) {
  const digits = chileLocalDigits(value)
  return digits ? formatChileMobile(value) : '—'
}

function ejecutivoActivo(usuario) {
  const value = usuario?.ESTADO ?? usuario?.estado
  if (value === true || value === 1 || value === '1') return true
  const n = Number(value)
  return Number.isFinite(n) && n === 1
}

function opcionesUnicas(rows, ...keys) {
  const values = new Set()
  rows.forEach((row) => {
    const value = String(pick(row, ...keys)).trim()
    if (value) values.add(value)
  })
  return [...values].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
}

function editFromRow(usuario) {
  return {
    email: String(pick(usuario, 'MAIL', 'mail', 'email')),
    nombre: String(pick(usuario, 'NOMBRE', 'nombre')),
    tipo_usuario: String(pick(usuario, 'TIPO_USUARIO', 'tipo_usuario')),
    tipo_ejecutivo: String(pick(usuario, 'TIPO_EJECUTIVO', 'tipo_ejecutivo')),
    grupo: String(pick(usuario, 'GRUPO', 'grupo')),
    sucursal: String(pick(usuario, 'SUCURSAL', 'sucursal')),
    ejec_sis_fact: String(pick(usuario, 'EJEC_SIST_FACTORING', 'ejec_sis_fact', 'ejecutivo_sistema_factoring')),
    fecha_ingreso: fechaInput(pick(usuario, 'FECHA_INGRESO', 'fecha_ingreso')),
    telefono: formatChileMobile(pick(usuario, 'TELEFONO', 'telefono')),
  }
}

const GestionUsuarios = () => {
  const { realUser } = useUser()
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [desactivando, setDesactivando] = useState(null)
  const [edit, setEdit] = useState(null)
  const [saving, setSaving] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM)
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordOk, setPasswordOk] = useState(false)
  const [editMessage, setEditMessage] = useState('')
  const [pageMessage, setPageMessage] = useState('')
  const [equipos, setEquipos] = useState([])
  const [tiposUsuario, setTiposUsuario] = useState([])
  const [creando, setCreando] = useState(false)
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroGrupo, setFiltroGrupo] = useState('')
  const [filtroSucursal, setFiltroSucursal] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [pageError, setPageError] = useState('')

  const loadUsuarios = useCallback(async () => {
    const data = await getUsuarios(true)
    const usuariosArray = Array.isArray(data) ? data : data?.data || []
    setUsuarios(usuariosArray)
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        await loadUsuarios()
      } catch (error) {
        console.error('Error cargando usuarios:', error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [loadUsuarios])

  useEffect(() => {
    if (!creando) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setCreando(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [creando])

  useEffect(() => {
    const loadSelectData = async () => {
      try {
        const [equiposData, tiposData] = await Promise.all([
          getEquipos(),
          getTiposUsuario()
        ])
        const equiposArray = Array.isArray(equiposData) ? equiposData :
                           equiposData?.data || equiposData?.equipos || []
        const tiposArray = Array.isArray(tiposData) ? tiposData :
                          tiposData?.data || tiposData?.tipos || []
        setEquipos(equiposArray)
        setTiposUsuario(tiposArray)
      } catch (error) {
        console.error('Error cargando selects:', error)
      }
    }
    loadSelectData()
  }, [])

  const tiposEnTabla = useMemo(
    () => opcionesUnicas(usuarios, 'TIPO_USUARIO', 'tipo_usuario'),
    [usuarios],
  )
  const gruposEnTabla = useMemo(
    () => opcionesUnicas(usuarios, 'GRUPO', 'grupo'),
    [usuarios],
  )
  const sucursalesEnTabla = useMemo(
    () => opcionesUnicas(usuarios, 'SUCURSAL', 'sucursal'),
    [usuarios],
  )

  const filtrados = useMemo(() => {
    const porEstado = usuarios.filter((usuario) => {
      if (filtroEstado === 'activos') return ejecutivoActivo(usuario)
      if (filtroEstado === 'inactivos') return !ejecutivoActivo(usuario)
      return true
    })
    const porSelects = porEstado.filter((usuario) => {
      if (filtroTipo && String(pick(usuario, 'TIPO_USUARIO', 'tipo_usuario')) !== filtroTipo) return false
      if (filtroGrupo && String(pick(usuario, 'GRUPO', 'grupo')) !== filtroGrupo) return false
      if (filtroSucursal && String(pick(usuario, 'SUCURSAL', 'sucursal')) !== filtroSucursal) return false
      return true
    })
    return filterData(porSelects, busqueda, (usuario) => [
      pick(usuario, 'NOMBRE', 'nombre'),
      pick(usuario, 'MAIL', 'mail', 'email'),
      pick(usuario, 'TELEFONO', 'telefono'),
      telefonoTabla(pick(usuario, 'TELEFONO', 'telefono')),
      pick(usuario, 'TIPO_USUARIO', 'tipo_usuario'),
      pick(usuario, 'GRUPO', 'grupo'),
      pick(usuario, 'SUCURSAL', 'sucursal'),
      pick(usuario, 'TIPO_EJECUTIVO', 'tipo_ejecutivo'),
      ejecutivoActivo(usuario) ? 'Activo' : 'Inactivo',
    ])
  }, [usuarios, filtroEstado, filtroTipo, filtroGrupo, filtroSucursal, busqueda])

  const totalPages = Math.max(1, Math.ceil(filtrados.length / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const desde = filtrados.length === 0 ? 0 : (pageSafe - 1) * pageSize + 1
  const hasta = Math.min(pageSafe * pageSize, filtrados.length)
  const pagina = filtrados.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  useEffect(() => {
    setPage(1)
  }, [busqueda, filtroEstado, filtroTipo, filtroGrupo, filtroSucursal, pageSize])

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroEstado('todos')
    setFiltroTipo('')
    setFiltroGrupo('')
    setFiltroSucursal('')
    setPage(1)
  }

  const handleCambiarEstado = async (email, estado) => {
    setDesactivando(email)
    setPageMessage('')
    setPageError('')
    try {
      const result = await cambiarEstadoUsuario(email, estado)
      const nextEstado = Number(result?.estado) === 1 ? 1 : Number(estado) === 1 ? 1 : 0
      try {
        await loadUsuarios()
      } catch (reloadError) {
        console.error('Error recargando usuarios:', reloadError)
      }
      if (result?.estado === 0 || result?.estado === 1) {
        setUsuarios((prev) =>
          prev.map((usuario) => {
            const mail = String(pick(usuario, 'MAIL', 'mail', 'email')).trim().toLowerCase()
            if (mail !== String(email).trim().toLowerCase()) return usuario
            return { ...usuario, ESTADO: nextEstado, estado: nextEstado }
          }),
        )
      }
      setPageMessage(nextEstado === 1 ? 'Ejecutivo habilitado' : 'Ejecutivo deshabilitado')
    } catch (error) {
      setPageError(error?.message || 'No se pudo cambiar el estado')
      console.error('Error cambiando estado:', error)
    } finally {
      setDesactivando(null)
    }
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEdit((prev) => ({
      ...prev,
      [name]: name === 'telefono' ? formatChileMobile(value) : value
    }))
  }

  const openEdit = (usuario) => {
    setEditMessage('')
    setPasswordMessage('')
    setPasswordOk(false)
    setPasswordForm(EMPTY_PASSWORD_FORM)
    setEdit(editFromRow(usuario))
  }

  const closeEdit = () => {
    if (saving || savingPassword) return
    setEdit(null)
    setPasswordForm(EMPTY_PASSWORD_FORM)
    setPasswordMessage('')
    setPasswordOk(false)
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordForm((prev) => ({ ...prev, [name]: value }))
    setPasswordOk(false)
  }

  const handleResetClave = async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (!edit || savingPassword || saving) return

    setPasswordMessage('')
    setPasswordOk(false)

    const nuevo = String(passwordForm.nuevo || '')
    const confirmar = String(passwordForm.confirmar || '')
    const operadorPass = String(passwordForm.operador || '')
    const emailOperador = String(realUser?.email || realUser?.mail || realUser?.MAIL || '').trim()

    if (!emailOperador) {
      setPasswordMessage('No se pudo identificar al administrador de la sesión')
      return
    }
    if (nuevo.length < MIN_LEN_CLAVE) {
      setPasswordMessage(`La nueva clave debe tener al menos ${MIN_LEN_CLAVE} caracteres`)
      return
    }
    if (nuevo !== confirmar) {
      setPasswordMessage('La confirmación no coincide con la nueva contraseña')
      return
    }
    if (!operadorPass) {
      setPasswordMessage('Ingresa tu contraseña para autorizar el cambio')
      return
    }

    setSavingPassword(true)
    try {
      const result = await resetearClaveUsuario({
        email: edit.email,
        password_nuevo: nuevo,
        email_operador: emailOperador,
        password_operador: operadorPass,
      })
      setPasswordForm(EMPTY_PASSWORD_FORM)
      setPasswordOk(true)
      setPasswordMessage(result?.message || 'Contraseña actualizada en Cognito')
    } catch (error) {
      setPasswordMessage(error?.message || 'No se pudo cambiar la contraseña')
      console.error('Error cambiando clave:', error)
    } finally {
      setSavingPassword(false)
    }
  }

  const applyLocalEdit = (prev, saved) =>
    prev.map((u) => {
      const mail = pick(u, 'MAIL', 'mail', 'email')
      if (String(mail) !== String(saved.email)) return u
      return {
        ...u,
        NOMBRE: saved.nombre,
        MAIL: saved.email,
        TIPO_USUARIO: saved.tipo_usuario,
        GRUPO: saved.grupo,
        TELEFONO: saved.telefono,
        TIPO_EJECUTIVO: saved.tipo_ejecutivo,
        SUCURSAL: saved.sucursal,
        EJEC_SIST_FACTORING: saved.ejec_sis_fact,
        FECHA_INGRESO: saved.fecha_ingreso,
      }
    })

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (!edit || saving || savingPassword) return
    setEditMessage('')
    setPageMessage('')

    const hasPhoneDigits = chileLocalDigits(edit.telefono).length > 0
    if (hasPhoneDigits && !chileMobileCompleto(edit.telefono)) {
      setEditMessage('El teléfono debe tener 8 dígitos (56 9 1234 5678) o quedar vacío')
      return
    }

    const payload = {
      email: edit.email,
      nombre: edit.nombre,
      tipo_usuario: edit.tipo_usuario,
      tipo_ejecutivo: edit.tipo_ejecutivo,
      grupo: edit.grupo,
      sucursal: edit.sucursal,
      ejec_sis_fact: edit.ejec_sis_fact,
      fecha_ingreso: edit.fecha_ingreso,
      telefono: hasPhoneDigits ? chileMobileDigits(edit.telefono) : '',
    }

    setSaving(true)
    try {
      const result = await actualizarUsuario(payload)
      if (result?.success !== true) {
        setEditMessage(
          result?.message ||
            result?.error ||
            'El servidor no confirmó la actualización. Revisa que exista POST /gestion-usuarios/actualizar-usuario.'
        )
        return
      }
      try {
        await loadUsuarios()
      } catch (reloadError) {
        console.error('Error recargando usuarios:', reloadError)
      }
      setUsuarios((prev) => applyLocalEdit(prev, payload))
      setEdit(null)
      setPageMessage('Usuario actualizado')
    } catch (error) {
      setEditMessage(error?.message || 'Error al actualizar usuario')
      console.error('Error actualizando usuario:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Usuarios</h1>
          <p className="text-gray-600 text-sm mt-1">Crear y administrar usuarios del sistema</p>
        </div>
        <button
          type="button"
          onClick={() => setCreando(true)}
          className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md text-sm hover:bg-teal-700"
        >
          <MdPersonAdd className="w-4 h-4" />
          Nuevo usuario
        </button>
      </div>

      {creando && (
        <div
          className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setCreando(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-lg p-5 shadow-xl max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="nuevo-usuario-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="nuevo-usuario-title" className="text-lg font-bold text-gray-800 mb-4">
              Nuevo usuario
            </h2>
            <NuevoUsuario
              embedded
              onCancel={() => setCreando(false)}
              onCreated={async () => {
                try {
                  await loadUsuarios()
                } catch (error) {
                  console.error('Error recargando usuarios:', error)
                }
                setCreando(false)
                setPageMessage('Usuario creado')
              }}
            />
          </div>
        </div>
      )}

      {pageMessage && (
        <div className="mb-4 p-3 rounded bg-green-100 text-green-700 text-sm">{pageMessage}</div>
      )}
      {pageError && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-700 text-sm">{pageError}</div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex-1 min-w-[220px]">
          <span className="block text-xs font-medium text-gray-500 mb-1">Buscar</span>
          <span className="relative block">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, correo, teléfono, equipo…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </span>
        </label>
        <label className="min-w-[160px]">
          <span className="block text-xs font-medium text-gray-500 mb-1">Tipo de usuario</span>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Todos</option>
            {tiposEnTabla.map((tipo) => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[160px]">
          <span className="block text-xs font-medium text-gray-500 mb-1">Equipo</span>
          <select
            value={filtroGrupo}
            onChange={(e) => setFiltroGrupo(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Todos</option>
            {gruposEnTabla.map((grupo) => (
              <option key={grupo} value={grupo}>{grupo}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[160px]">
          <span className="block text-xs font-medium text-gray-500 mb-1">Sucursal</span>
          <select
            value={filtroSucursal}
            onChange={(e) => setFiltroSucursal(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Todas</option>
            {sucursalesEnTabla.map((sucursal) => (
              <option key={sucursal} value={sucursal}>{sucursal}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={limpiarFiltros}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
        >
          Limpiar
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {[
          ['todos', 'Todos'],
          ['activos', 'Activos'],
          ['inactivos', 'Inactivos'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFiltroEstado(id)}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              filtroEstado === id
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <p className="text-gray-500">Cargando usuarios...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo de Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grupo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagina.map((usuario, index) => {
                  const email = pick(usuario, 'MAIL', 'mail', 'email')
                  const activo = ejecutivoActivo(usuario)
                  return (
                  <tr key={email || index} className={activo ? '' : 'bg-gray-50 text-gray-400'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {pick(usuario, 'NOMBRE', 'nombre')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {telefonoTabla(pick(usuario, 'TELEFONO', 'telefono'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pick(usuario, 'TIPO_USUARIO', 'tipo_usuario')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pick(usuario, 'GRUPO', 'grupo')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => handleCambiarEstado(email, activo ? 0 : 1)}
                        disabled={desactivando === email || !email}
                        title={activo ? 'Deshabilitar ejecutivo' : 'Habilitar ejecutivo'}
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium disabled:opacity-50 ${
                          activo
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {desactivando === email ? 'Guardando…' : activo ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(usuario)}
                          className="bg-gray-100 text-gray-600 hover:bg-teal-100 hover:text-teal-700 p-2 rounded transition-colors"
                          title="Editar usuario"
                        >
                          <MdEdit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
            {usuarios.length === 0 && (
              <div className="text-center mt-4">
                <p className="text-gray-500">No hay usuarios registrados</p>
              </div>
            )}
            {usuarios.length > 0 && filtrados.length === 0 && (
              <div className="text-center mt-4">
                <p className="text-gray-500">Ningún usuario coincide con la búsqueda o los filtros</p>
              </div>
            )}
          </div>
        )}
        {!loading && filtrados.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
            <span>
              Mostrando {desde}–{hasta} de {filtrados.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={pageSafe === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                Anterior
              </button>
              <span>Página {pageSafe} de {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={pageSafe === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
            <label className="flex items-center gap-2">
              Filas
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1.5 border border-gray-300 rounded-md"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {edit && (
        <div
          className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={closeEdit}
        >
          <div
            className="bg-white rounded-xl w-full max-w-lg p-5 shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
          <form
            onSubmit={handleSaveEdit}
            action="#"
            method="post"
            noValidate
          >
            <h2 className="text-lg font-bold text-gray-800 mb-1">Editar usuario</h2>
            <p className="text-sm text-gray-500 mb-4">{edit.email}</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              name="nombre"
              value={edit.nombre}
              onChange={handleEditChange}
              required
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              className={`${INPUT_CLASS} mb-3 bg-gray-50 text-gray-500`}
              value={edit.email}
              readOnly
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              name="telefono"
              type="tel"
              inputMode="tel"
              maxLength={14}
              placeholder="56 9 1234 5678"
              value={edit.telefono}
              onChange={handleEditChange}
              onFocus={() => setEdit((prev) => ({ ...prev, telefono: formatChileMobile(prev.telefono) }))}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Usuario</label>
            <select
              className={`${INPUT_CLASS} mb-3`}
              name="tipo_usuario"
              value={edit.tipo_usuario}
              onChange={handleEditChange}
              required
            >
              <option value="">Seleccionar tipo</option>
              {edit.tipo_usuario && !tiposUsuario.some((t) => t.usuario === edit.tipo_usuario) && (
                <option value={edit.tipo_usuario}>{edit.tipo_usuario}</option>
              )}
              {tiposUsuario.map((tipo, index) => (
                <option key={tipo.usuario || index} value={tipo.usuario}>
                  {tipo.usuario}
                </option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Ejecutivo</label>
            <select
              className={`${INPUT_CLASS} mb-3`}
              name="tipo_ejecutivo"
              value={edit.tipo_ejecutivo}
              onChange={handleEditChange}
            >
              <option value="">Seleccionar tipo ejecutivo</option>
              {TIPO_EJECUTIVO.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Equipo</label>
            <select
              className={`${INPUT_CLASS} mb-3`}
              name="grupo"
              value={edit.grupo}
              onChange={handleEditChange}
              required
            >
              <option value="">Seleccionar equipo</option>
              {edit.grupo && !equipos.some((eq) => (eq.equipo || eq.usuario) === edit.grupo) && (
                <option value={edit.grupo}>{edit.grupo}</option>
              )}
              {equipos.map((equipo, index) => (
                <option key={equipo.equipo || equipo.usuario || index} value={equipo.equipo || equipo.usuario}>
                  {equipo.equipo || equipo.usuario}
                </option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
            <select
              className={`${INPUT_CLASS} mb-3`}
              name="sucursal"
              value={edit.sucursal}
              onChange={handleEditChange}
            >
              <option value="">Seleccionar sucursal</option>
              {edit.sucursal && !SUCURSALES.includes(edit.sucursal) && (
                <option value={edit.sucursal}>{edit.sucursal}</option>
              )}
              {SUCURSALES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Ejecutivo Sistema Factoring</label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              name="ejec_sis_fact"
              value={edit.ejec_sis_fact}
              onChange={handleEditChange}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Ingreso</label>
            <input
              className={`${INPUT_CLASS} mb-4`}
              type="date"
              name="fecha_ingreso"
              value={edit.fecha_ingreso}
              onChange={handleEditChange}
            />

            {editMessage && (
              <div className="mb-4 p-3 rounded bg-red-100 text-red-700 text-sm">{editMessage}</div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-3 py-2 text-sm border rounded-lg"
                onClick={closeEdit}
                disabled={saving || savingPassword}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                disabled={saving || savingPassword}
                onClick={handleSaveEdit}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>

          <form className="mt-5 pt-5 border-t border-gray-200" onSubmit={handleResetClave} noValidate>
            <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <MdLock className="w-4 h-4 text-teal-700" />
              Cambiar contraseña
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Se actualiza en Cognito. Mínimo {MIN_LEN_CLAVE} caracteres; la política del pool exige
              mayúscula, minúscula, número y símbolo.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              type="password"
              name="nuevo"
              autoComplete="new-password"
              value={passwordForm.nuevo}
              onChange={handlePasswordChange}
              disabled={savingPassword}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              type="password"
              name="confirmar"
              autoComplete="new-password"
              value={passwordForm.confirmar}
              onChange={handlePasswordChange}
              disabled={savingPassword}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tu contraseña de administrador
            </label>
            <input
              className={`${INPUT_CLASS} mb-3`}
              type="password"
              name="operador"
              autoComplete="current-password"
              placeholder="Autoriza el cambio"
              value={passwordForm.operador}
              onChange={handlePasswordChange}
              disabled={savingPassword}
            />

            {passwordMessage && (
              <div
                className={`mb-3 p-3 rounded text-sm ${
                  passwordOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {passwordMessage}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                disabled={savingPassword || saving}
              >
                <MdLock className="w-4 h-4" />
                {savingPassword ? 'Cambiando…' : 'Cambiar contraseña'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionUsuarios
